import type { ReactNode } from "react";

import type { IntlShape } from "react-intl";

import { Text } from "@/components/atoms/Text";

import { labelMessages } from "./priceSummaryLabels.messages";
import { Perspectives } from "./utils/consts";
import type { OutsideBaseMessage } from "./utils/types";

type OutsideBasePricingMessageProps = {
  intl: IntlShape;
  message: OutsideBaseMessage;
  /**
   * Adapter injects popover chrome around the base-pricing link text.
   * When omitted, the link renders as plain underlined text.
   */
  renderBasePricingLink?: (linkLabel: ReactNode) => ReactNode;
};

const wrapBasePricingLink = ({
  linkLabel,
  renderBasePricingLink,
}: {
  linkLabel: string;
  renderBasePricingLink?: (linkLabel: ReactNode) => ReactNode;
}): ReactNode =>
  renderBasePricingLink ? (
    renderBasePricingLink(linkLabel)
  ) : (
    <span className="tw-underline">{linkLabel}</span>
  );

/**
 * Inquiry outside-base copy. Popover / base-pricing breakdown stays adapter */
export const OutsideBasePricingMessage = ({
  intl,
  message,
  renderBasePricingLink,
}: OutsideBasePricingMessageProps) => {
  const { minimumDuration, perspective } = message;

  if (perspective === Perspectives.Host) {
    const linkNode = wrapBasePricingLink({
      linkLabel: intl.formatMessage(labelMessages.HostBasePricingLink, {
        duration: minimumDuration,
      }),
      renderBasePricingLink,
    });

    return (
      <Text typeface="body-1-long" element="p" className="tw-mt-8">
        {intl.formatMessage(labelMessages.OutsideBaseHostMessage, {
          basePricingLink: linkNode,
        })}
      </Text>
    );
  }

  const linkNode = wrapBasePricingLink({
    linkLabel: intl.formatMessage(labelMessages.SeeBasePricingLink, {
      duration: minimumDuration,
    }),
    renderBasePricingLink,
  });

  return (
    <Text typeface="body-1-long" element="p" className="tw-mt-8">
      {intl.formatMessage(labelMessages.OutsideBaseGuestMessage, {
        duration: minimumDuration,
      })}{" "}
      {linkNode}
    </Text>
  );
};
